import React, { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import PromoBanner from '@wix/da-ds/pkg/PromoBanner';
import PromoBadge from '@wix/da-ds/pkg/Badges/PromoBadge';
import { useCurrentPromo } from '@wix/da-shared-react/pkg/utils/hooks/useCurrentPromo';
import EmptyState from '../../../EmptyState';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import TierCreatorReduxModule from '@wix/da-shared-react/pkg/Tier/redux/creator/ModuleLoadable';
import BuyCoreButton from '@wix/da-shared-react/pkg/Button/BuyCoreButton';
import s from './SubscriptionsCoreOnlyBlock.scss';

export interface Props {
  className?: string;
  hasCore: boolean;
  createTier: () => void;
  createTipJar: () => void;
}

const SubscriptionsCoreOnlyBlock: React.FC<Props> = ({
  className,
  hasCore,
  createTier,
  createTipJar,
}) => {
  const isMobile = useContext(MobileContext);
  const currentPromo = useCurrentPromo('self');
  const { t } = useTranslation();

  let variant;
  let cta;
  let secondaryCta;
  // eslint-disable-next-line default-case
  switch (true) {
    case hasCore:
      variant = 'hasCore';
      cta = (
        <SolidButton
          variant="approval-green"
          size="medium"
          priority="primary"
          onClick={createTier}
          className={s['button']}
          biData={{
            evid: BiEventType.TIER_ADD_NEW,
            is_empty_state_view: 1,
            widgetname: undefined,
          }}
        >
          {t('page.subscriptions.coreOnlyBlock.CTAbutton.createTier')}
        </SolidButton>
      );
      secondaryCta = (
        <GhostButton
          variant="main"
          size="medium"
          onClick={createTipJar}
          className={s['button']}
          biData={{
            evid: BiEventType.TIER_ADD_NEW,
            is_empty_state_view: 1,
            is_tip_jar: 1,
            widgetname: undefined,
          }}
        >
          {t('page.subscriptions.coreOnlyBlock.CTAbutton.createTipJar')}
        </GhostButton>
      );
      break;
    case !hasCore:
      variant = 'noCore';
      cta = (
        <BuyCoreButton
          size="medium"
          className={s['button']}
          biData={{
            referral_info: ReferralType.SUBSCRIPTIONS,
          }}
        >
          {t('page.subscriptions.coreOnlyBlock.CTAbutton.buyCore')}
        </BuyCoreButton>
      );
      secondaryCta = (
        <GhostButton
          variant="main"
          size="medium"
          onClick={createTier}
          className={s['button']}
          biData={{
            evid: BiEventType.TIER_ADD_NEW,
            is_empty_state_view: 1,
            widgetname: undefined,
          }}
        >
          {t('page.subscriptions.coreOnlyBlock.CTAbutton.startCreatingATier')}
        </GhostButton>
      );
      break;
  }

  return (
    <div className={s['root']}>
      <TierCreatorReduxModule />
      {!hasCore && !currentPromo && (
        <PromoBanner
          className={s['promo-strip']}
          icon={<PromoBadge badgeType="tier" size={24} />}
          size={isMobile ? 'small' : 'big'}
          variant="subscription"
          messageAfterIcon={t('page.subscriptions.promoStrip.tipJar.message')}
          ctaLabel={t('page.subscriptions.promoStrip.tipJar.CTAbutton')}
          ctaOnClick={createTipJar}
          ctaBiData={{
            evid: BiEventType.TIER_ADD_NEW,
            is_empty_state_view: 1,
            is_tip_jar: 1,
            widgetname: undefined,
          }}
        />
      )}
      <EmptyState
        className={c(s['empty-state-block'], className)}
        title={t(
          `page.subscriptions.coreOnlyBlock.title.var.${variant}.updatedTiers`
        )}
        subtitle={
          <div className={s['subtitle-wrapper']}>
            <div className={s['subtitle']}>
              {t(
                `page.subscriptions.coreOnlyBlock.subtitle.var.${variant}.updatedTiers`
              )}
            </div>
            {hasCore && (
              <TextButton
                size="medium"
                variant="main"
                href={t('widgets.tierContent.learnMore.url')}
              >
                {t('page.subscriptions.coreOnlyBlock.CTAbutton.learnMore')}
              </TextButton>
            )}
          </div>
        }
        illustration={<div className={s['illustration']} />}
        withIllustrationBelow
        customButton={cta}
        customSecondaryButton={secondaryCta}
      />
    </div>
  );
};
SubscriptionsCoreOnlyBlock.displayName = 'SubscriptionsCoreOnlyBlock';

export default SubscriptionsCoreOnlyBlock;
